import React, { useEffect, useMemo } from 'react';
import { Icon } from './Icons.jsx';
import { formatExponential } from '../lib/format.js';

const LEVEL_LABEL = {
  info: '信息',
  warning: '告警',
  error: '错误',
  repair: '修复',
  warn: '告警',
};

function clockOf(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('zh-CN', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function DiagnosticsDrawer({
  open,
  onClose,
  snapshot,
  selfTest,
  onRunSelfTest,
  uiIncidents = [],
  onClearLogs,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, open]);

  const logs = useMemo(() => {
    const runtime = (snapshot?.diagnostics ?? []).map((entry) => ({ ...entry, source: 'runner' }));
    const interfaces = uiIncidents.map((entry) => ({ ...entry, source: 'ui' }));
    return [...runtime, ...interfaces].sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 40);
  }, [snapshot?.diagnostics, uiIncidents]);

  if (!open) return null;

  const summary = selfTest?.summary;
  const failed = selfTest?.results.filter((item) => !item.passed) ?? [];

  return (
    <div className="drawer-layer" role="dialog" aria-modal="true" aria-label="运行诊断与自动修复">
      <button type="button" className="drawer-scrim" onClick={onClose} aria-label="关闭诊断面板" />
      <aside className="diagnostics-drawer">
        <header className="drawer-head">
          <div>
            <div className="eyebrow">
              <Icon name="terminal" size={14} />
              DIAGNOSTICS & SELF-HEALING
            </div>
            <h2>运行诊断与自动修复</h2>
            <p>守恒监测、参数护栏、步进异常回滚与界面错误捕获统一汇入这里。</p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="关闭">
            <Icon name="x" size={19} />
          </button>
        </header>

        <div className="drawer-summary">
          <div className={`summary-tile ${summary?.ok ? 'ok' : failed.length ? 'danger' : ''}`}>
            <span>内置自检</span>
            <strong>{summary ? summary.label : '—'}</strong>
            <small>{summary?.ok ? '全部通过' : '存在未通过项'}</small>
          </div>
          <div className={`summary-tile ${snapshot?.repairs ? 'warn' : ''}`}>
            <span>自动修复</span>
            <strong>{snapshot?.repairs ?? 0}</strong>
            <small>参数 / 步进 / 守恒</small>
          </div>
          <div className={`summary-tile ${snapshot?.errors ? 'danger' : ''}`}>
            <span>捕获错误</span>
            <strong>{snapshot?.errors ?? 0}</strong>
            <small>步进异常与安全暂停</small>
          </div>
          <div className="summary-tile">
            <span>积分步长</span>
            <strong>{snapshot ? `${(1 / snapshot.fixedDt).toFixed(0)}` : '—'}</strong>
            <small>Hz · 自适应细化</small>
          </div>
        </div>

        <section className="drawer-section">
          <div className="drawer-section-head">
            <div>
              <strong>守恒与护栏自检</strong>
              <small>浏览器内重新积分四类模型，失败时自动触发稳定性修复。</small>
            </div>
            <button type="button" className="secondary-btn compact" onClick={onRunSelfTest}>
              <Icon name="refresh" size={14} />
              重新运行
            </button>
          </div>
          {selfTest ? (
            <div className="selftest-list">
              {selfTest.results.map((item) => (
                <div className={`selftest-row ${item.passed ? 'ok' : 'fail'}`} key={item.name}>
                  <span className="selftest-icon">
                    <Icon name={item.passed ? 'check' : 'alert'} size={14} />
                  </span>
                  <div className="selftest-copy">
                    <strong>{item.name}</strong>
                    <small>{item.detail}</small>
                  </div>
                  <div className="selftest-metric">
                    <span>{formatExponential(item.metric, 2)}</span>
                    <small>阈值 {formatExponential(item.tolerance, 2)}</small>
                  </div>
                  <span className={`selftest-state ${item.passed ? 'ok' : 'fail'}`}>
                    {item.passed ? '通过' : '失败'}
                  </span>
                </div>
              ))}
              {selfTest.repaired && (
                <div className="repaired-note">
                  <Icon name="refresh" size={14} />
                  自检发现异常后，已自动减小积分步长并刷新守恒基线。
                </div>
              )}
            </div>
          ) : (
            <p className="muted-copy">尚未运行自检。</p>
          )}
        </section>

        <section className="drawer-section logs-section">
          <div className="drawer-section-head">
            <div>
              <strong>运行日志</strong>
              <small>最新 {logs.length} 条 · 含物理步进、参数护栏与界面异常。</small>
            </div>
            {onClearLogs && (
              <button type="button" className="text-btn muted" onClick={onClearLogs}>
                <Icon name="trash" size={14} />
                清空
              </button>
            )}
          </div>
          <div className="log-list">
            {logs.length === 0 && <p className="muted-copy">暂无日志。</p>}
            {logs.map((entry) => (
              <article className={`log-entry ${entry.level}`} key={entry.id}>
                <div className="log-rail">
                  <span className={`log-dot ${entry.level}`} />
                </div>
                <div className="log-body">
                  <div className="log-top">
                    <span className={`log-level ${entry.level}`}>{LEVEL_LABEL[entry.level] ?? entry.level}</span>
                    <strong>{entry.title}</strong>
                    <time>{clockOf(entry.at)}</time>
                  </div>
                  {entry.detail && <p>{entry.detail}</p>}
                  {entry.action && (
                    <div className="log-action">
                      <Icon name="arrowRight" size={12} />
                      {entry.action}
                    </div>
                  )}
                  <span className="log-code">{entry.code}</span>
                </div>
              </article>
            ))}
          </div>
        </section>

        <footer className="drawer-foot">
          <div>
            <Icon name="shield" size={16} />
            <span>自动修复顺序：参数夹紧 → 状态回滚 → 步长减半 → 守恒回滚 → 安全暂停。</span>
          </div>
          <button type="button" className="ghost-btn" onClick={onClose}>
            关闭
          </button>
        </footer>
      </aside>
    </div>
  );
}
